import { RangeSlider } from '@mantine/core'
import { useStatsigClient } from '@statsig/react-bindings'
import React, { useEffect, useState } from 'react'
import { useRange } from 'react-instantsearch'
import { Input } from 'components/Input'
import { Box, Flex } from 'components/Layout'
import { Text } from 'components/Typography'
import { STATSIG_EVENTS } from 'providers/statsig'
import { FONT_SIZE } from 'Theme'
import { FilterCollapsibleItem } from './FilterCollapsibleItem'

type FilterCollapsibleRangeProps = {
  title: string
  defaultOpened?: boolean
  attribute: string
  suffix?: string
  showInputs?: boolean
  inputSuffix?: string
}

export const FilterCollapsibleRange = ({
  title,
  defaultOpened,
  attribute,
  suffix,
  showInputs = false,
  inputSuffix,
}: FilterCollapsibleRangeProps) => {
  const { client: statsigClient } = useStatsigClient()
  const { start, range, canRefine, refine } = useRange({ attribute })
  const { min, max } = range

  const from = Math.max(min, Number.isFinite(start[0]) ? start[0] : min)
  const to = Math.min(max, Number.isFinite(start[1]) ? start[1] : max)

  const [value, setValue] = useState<[number, number]>([from, to])
  const debounceRef = React.useRef<NodeJS.Timeout | null>(null)

  const handleInputChange = (index: 0 | 1, newVal: number) => {
    const next = [...value] as [number, number]
    next[index] = newVal
    setValue(next)

    // debounce refinement
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    debounceRef.current = setTimeout(() => {
      refine(next)
    }, 2000) // 2 seconds debounce
  }

  useEffect(() => {
    setValue([from, to])
  }, [from, to])

  if (!canRefine) {
    return null
  }

  return (
    <FilterCollapsibleItem
      title={title}
      defaultOpened={defaultOpened}
      onClick={() => {
        if (statsigClient) {
          // refactor in future
          if (attribute === 'depths') {
            statsigClient.logEvent(STATSIG_EVENTS.FILTER_DIMENSIONS_DEPTHS)
          }
          if (attribute === 'widths') {
            statsigClient.logEvent(STATSIG_EVENTS.FILTER_DIMENSIONS_WIDTHS)
          }
          if (attribute === 'heights') {
            statsigClient.logEvent(STATSIG_EVENTS.FILTER_DIMENSIONS_HEIGHTS)
          }
        }
      }}
    >
      <Box>
        <Flex justifyContent="space-between" mb={2}>
          <Text style={{ fontSize: FONT_SIZE.PX_14 }}>
            {min}
            {suffix}
          </Text>
          <Text style={{ fontSize: FONT_SIZE.PX_14 }}>
            {max}
            {suffix}
          </Text>
        </Flex>

        <RangeSlider
          label={null}
          min={min}
          max={max}
          value={value}
          onChange={setValue}
          onChangeEnd={refine}
          disabled={!canRefine}
          step={1}
          minRange={0}
        />
        {showInputs && (
          <Flex
            justifyContent="space-between"
            alignItems="center"
            mt={12}
            gap={40}
          >
            <Box width="100%">
              <Text fontSize={FONT_SIZE.PX_12}>Min {inputSuffix}</Text>
              <Input
                type="number"
                min={from}
                max={to}
                value={value[0]}
                onChange={(e) => handleInputChange(0, Number(e.target.value))}
                disabled={!canRefine}
              />
            </Box>

            <Box width="100%">
              <Text fontSize={FONT_SIZE.PX_12}>Max {inputSuffix}</Text>
              <Input
                type="number"
                min={from}
                max={to}
                value={value[1]}
                onChange={(e) => handleInputChange(1, Number(e.target.value))}
                disabled={!canRefine}
              />
            </Box>
          </Flex>
        )}
      </Box>
    </FilterCollapsibleItem>
  )
}
